import isDeepEqual from '@/lib/isDeepEqual.js';
import flattenWorkflowSteps from '@/lib/workflows/flattenWorkflowSteps.js';
import listWorkflowTestConfigFields from '@/lib/workflows/listWorkflowTestConfigFields.js';

const parse = (text) => {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
};

/*
 * The person's choices in the Test window, as the `test.steps` the engine takes. Only what differs from the
 * workflow as built is sent: a JSON setting
 * replaces its whole field, an option setting (code, prompt, flow control) one key of the step's options, plus
 * a loop's picked records and a branch's answer. JSON that doesn't parse is listed in `invalid` as { stepId, key }
 * and holds its whole step back, so the test can't run until it's fixed. A past choice that no longer fits the
 * draft is listed in `dropped` as { stepId, reason, … } and sends nothing.
 */
const buildWorkflowTestRequestSteps = ({ choices = {}, steps = [] } = {}) => {
  const stepById = Object.fromEntries(flattenWorkflowSteps(steps).map((step) => [step.stepId, step]));
  const requestSteps = {};
  const invalid = [];
  const dropped = [];

  for (const [stepId, choice] of Object.entries(choices)) {
    const step = stepById[stepId];

    if (choice?.dropped) {
      dropped.push({ stepId, ...choice.dropped });
      continue;
    }

    if (!step) {
      continue;
    }

    const request = {};
    if (choice.disabled === true) {
      request.disabled = true;
    }
    const stepInvalid = [];
    const config = {};
    // Whole fields first, then the option keys that have a field of their own on top of them.
    const fields = listWorkflowTestConfigFields(step).sort((left, right) => Number(left.target.startsWith('options.')) - Number(right.target.startsWith('options.')));
    let options;

    for (const field of fields) {
      const text = choice.config?.[field.key];

      if (text === undefined || text === field.saved) {
        continue;
      }

      const parsed = field.kind === 'text' ? { ok: true, value: text } : parse(text);

      if (!parsed.ok) {
        stepInvalid.push({ stepId, key: field.key });
      } else if (field.target === 'options') {
        const current = options ?? step.options ?? {};
        options = { ...parsed.value, ...Object.fromEntries((field.omitKeys || []).filter((key) => current[key] !== undefined).map((key) => [key, current[key]])) };
      } else if (field.target.startsWith('options.')) {
        options = { ...(options ?? step.options), [field.target.slice('options.'.length)]: parsed.value };
      } else if (!isDeepEqual(parsed.value, step[field.target] ?? {})) {
        config[field.target] = parsed.value;
      }
    }

    if (options !== undefined && !isDeepEqual(options, step.options ?? {})) {
      config.options = options;
    }

    if (stepInvalid.length > 0) {
      invalid.push(...stepInvalid);
      continue;
    }

    if (Object.keys(config).length > 0) {
      request.config = config;
    }

    if (step.type === 'loop' && Array.isArray(choice.items)) {
      request.items = choice.items;
    }

    if (['branch', 'router'].includes(step.type) && choice.answer) {
      request.answer = choice.answer;
    }

    if (Object.keys(request).length > 0) {
      requestSteps[stepId] = request;
    }
  }

  return { steps: requestSteps, invalid, dropped };
};

export default buildWorkflowTestRequestSteps;
